<template lang="pug">
.view-in-ar
  .overlay(ref="overlayTutorial" :class="{'visible': showArOverlay}")
    ar-overlay(v-if="showArOverlay" :xr="xr" :engine="engine")
  .link(@click="uiStore.showOverlay('ar-disclaimer', rogue)") {{ __('View AR Experience Information Notice') }}
  .threed-viewer(:class="{showing: overlay === 'show3d', loaded: !rackbuilderLoading} ")
    button.a11y.modal-close-button(:aria-label="__('Close 3D viewer')" @click="toggle3dView(false)")
      Icon( name="x" size="s" fill="black")
    .threejs(ref="three" key="ar-view-btn")
    .loading(v-show="rackbuilderLoading")
      spinner
  Modal.ar-modal(v-if="overlay === 'ar-disclaimer'" @close="uiStore.hideOverlay()")
    .ar-modal-content
      h1.center {{ __('AR Experience Information Notice') }}
      p {{ __('The site is designed as a service to our customers and may be used for information purposes only. All items are subject to modification, change and/or being withdrawn at any time in the sole discretion of Rogue Fitness.') }}
      p {{ __('Any renderings and depictions in this Website are conceptual only and are for the convenience of reference. They should not be relied upon as representations, express or implied, of the final detail of the Units. Rogue expressly reserves the right to make modifications, revisions, and changes it deems desirable in its sole and absolute discretion. Dimensions are approximate and may vary with actual construction.') }}
      p {{ __('The project graphics, renderings and text provided herein are copyrighted works owned by Rogue. All rights reserved. Unauthorized reproduction, display or other dissemination of such materials is strictly prohibited and constitutes copyright infringement.') }}
      p {{ __('All communication or material transmitted by you through this Website shall be treated as non-confidential and non-proprietary.') }}
      RogueButton(@click="uiStore.hideOverlay()" width="full") {{ __('Acknowledge') }}
  Modal.ar-modal(v-if="(overlay === 'ar-qr' && qrImg)" @close="uiStore.hideOverlay()")
    .ar-modal-content
      h1.center {{ __('View on your mobile device') }}
      img.qr-img(:src="qrImg" :alt="__('QR code to view this product in augmented reality')")
      p {{ __('In order to view this product in augmented reality, scan the QR code using the camera on your phone or tablet.') }}
      RogueButton(@click="uiStore.hideOverlay()" width="full") {{ __('Close Window') }}
  .buttons
    RogueButton(
      @click="viewInAr()"
      color="black"
      :loading="loading"
      width="full"
    )
      .btn-content
        Icon.ar-icon(name="rackbuilder/ar" fill="white")
        div {{ __('View in Your Room') }}
    RogueButton.three-d(
      @click="toggle3dView(true)"
      color="black"
      width="full"
      v-if="(rogue.featureFlags.isOn('rhpa-3d') && !hide3dButton)"
    )
      .btn-content
        Icon.ar-icon(name="rackbuilder/3d" fill="white")
        div {{ __('View in 3D') }}
</template>
<script setup lang="ts">
import { ref, shallowRef, nextTick, onMounted, computed, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import type { RogueEngineOptions, PartDefinition } from '@rogue/rackbuilder'
import { allowScroll, preventScroll } from 'sisu/js/body-scroll'
import { useRackbuilder } from '../../composables/RackbuilderComposable'
import { use3DEngine, getSharedEngine, PDP_ENGINE_KEY } from '../../composables/RogueEngineComposable'
import arOverlay from './ar-overlay.vue'
import { useUIStore } from '@/js/stores/uiStore'
import { getBuyRequestMeta } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest } from '@/js/services/types/order'

type RogueXRModule = any

const props = defineProps<{
  buyRequest?: BuyRequest,
  part?: PartDefinition
  engineConfig?: RogueEngineOptions
  hide3dButton?: boolean
}>()

const uiStore = useUIStore()
const rogue = useRogue()
const route = useRoute()

const loading = ref(false)
const showArOverlay = ref(false)
const show3d = ref(false)

const hide3dButton = computed(() => {
  const productId = props.buyRequest?.metadata?.groupedProductId || props.buyRequest?.productId || 0
  const product = rogue.getProductById(productId)

  return product && (product.roguebuilder?.props?.rackbuilder?.area === 'main')
})

const productSku = computed(() => {
  const product = rogue.getProductById(props.buyRequest?.productId || 0)

  return product.sku
})

const overlay = computed(() => uiStore.overlay)

const overlayTutorial = ref<HTMLElement>()
const three = ref<HTMLDivElement>()

const qrImg = ref('')

function getProductLink() {
  let buyRequest = props.buyRequest

  if (!buyRequest) {
    return false
  }

  if (buyRequest.type === 'group') {
    buyRequest = buyRequest.children?.[0]
  }

  if (!buyRequest) {
    return false
  }

  return getBuyRequestMeta(buyRequest, rogue).link
}

const { engine } = use3DEngine({ container: three, engineOptions: props.engineConfig, autoInit: false })

const rack = useRackbuilder({ 
  buyRequest: props.buyRequest, 
  engine, 
})
const rackbuilderLoading = rack.loading

function toggle3dView(newVal: boolean) {
  if (newVal) {
    uiStore.showOverlay('show3d', rogue)
    preventScroll()

    nextTick(() => engine.init())
  } else {
    uiStore.hideOverlay()
    allowScroll()
  }
}

async function generateQRCode() {
  const link = getProductLink()
  if (!link) {
    return false
  }

  const qr = await import('qrcode')

  const url = new URL(link)
  url.searchParams.set('ar', '1')
  const r = (Math.random() + 1).toString(36).substring(7)
  url.searchParams.set('sid', r)

  const finalLink = url.toString().replace('https://', '')

  rogue.track.track({ type: 'AR', link, step: 'Show QR', sid: r, sku: productSku.value })

  qrImg.value = await qr.toDataURL(finalLink, { width: 1000 })

  return true
}

const xr = shallowRef<RogueXRModule | null>(null)

async function viewInAr() {
  loading.value = true
  const userAgent = window.navigator.userAgent
  const isIpad = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1
  // ?debugAR=1 forces the iOS USDZ export path on desktop for local testing
  const debugAR = route.query.debugAR === '1'
  const isIOS = debugAR || rogue.appContext?.platform === 'ios' || /iPad|iPhone|iPod/i.test(userAgent) || isIpad

  const isAndroid = /Android/i.test(userAgent)
  const isAndroidApp = rogue.appContext?.platform === 'android'
  const isAndroidMobile = isAndroid && !isAndroidApp
  const isChromeIOS = /CriOS/i.test(userAgent)
  const isEdgeIOS = /EdgiOS/i.test(userAgent)
  const isFirefoxIOS = /FxiOS/i.test(userAgent)
  
  // iOS 18+ has issues with WebXR - force native AR QuickLook on iOS regardless of WebXR support
  // This prevents WebGL context loss and black overlay crashes
  let hasXR = false
  if ((navigator as any).xr && !isIOS) {
    try {
      hasXR = await (navigator as any).xr.isSessionSupported('immersive-ar') || 
              await (navigator as any).xr.isSessionSupported('immersive-vr')
    } catch (e) {
      rogue.log.warn('WebXR support check failed', e)
    }
  }

  const cachedEngine = getSharedEngine(PDP_ENGINE_KEY)
  if (cachedEngine) {
    rogue.log.info('Found shared engine')
  }

  const activeEngine = cachedEngine || engine
  let sid = (route.query.sid || '').toString()

  // Determine if iOS AR is supported
  let iosAR = false
  let useDirectDownload = debugAR // on desktop always download the file directly
  if (isIOS) {
    if (debugAR) {
      // Desktop debug mode — skip Quick Look, download the USDZ so it can be
      // inspected in Reality Composer / Preview to verify export correctness
      iosAR = true
    } else if (rogue.appContext?.platform === 'ios') {
      iosAR = true
    } else if (isChromeIOS || isEdgeIOS || isFirefoxIOS) {
      // Third-party browsers on iOS need to trigger direct download with proper MIME type
      iosAR = true
      useDirectDownload = true
    } else {
      const a = document.createElement('a')
      if (a.relList.supports('ar')) {
        iosAR = true
      } else {
        rogue.log.warn('iOS device without AR support')
      }
    }
  }

  const dataCallback = rogue.isApp() ? async (model: Blob, filename: string, extension: string) => {
    try {
      const fileSize = model.size
      // Small chunks to minimize peak memory. We'll carry a 0-2 byte remainder to preserve 3-byte boundaries.
      const baseChunkSize = 64 * 1024
      let offset = 0
      let remainderBytes: number[] = []
      const base64Parts: string[] = []

      while (offset < fileSize) {
        const end = Math.min(offset + baseChunkSize, fileSize)
        const slice = model.slice(offset, end)
        const buf = new Uint8Array(await slice.arrayBuffer())

        // Prepend remainder
        let combined: number[]
        if (remainderBytes.length) {
          combined = remainderBytes.concat(Array.from(buf))
        } else {
          combined = Array.from(buf)
        }

        // Take length that is divisible by 3
        const usableLength = combined.length - (combined.length % 3)
        const chunkBytes = combined.slice(0, usableLength)
        remainderBytes = combined.slice(usableLength)

        // Encode bytes -> base64 without building a huge string first
        // Convert in manageable sub-blocks to avoid call stack limits
        const subBlock = 8 * 1024
        let binary = ''
        for (let i = 0; i < chunkBytes.length; i += subBlock) {
          const sub = chunkBytes.slice(i, i + subBlock)
          binary += String.fromCharCode.apply(null, sub as unknown as number[])
        }
        if (binary.length) {
          base64Parts.push(btoa(binary))
        }

        // Yield occasionally
        if ((offset / baseChunkSize) % 8 === 0) {
          await new Promise(resolve => setTimeout(resolve, 0))
        }

        offset = end
      }

      // Handle trailing remainder (0-2 bytes)
      if (remainderBytes.length) {
        const binary = String.fromCharCode.apply(null, remainderBytes as unknown as number[])
        base64Parts.push(btoa(binary))
      }

      // Join base64 and ensure padding
      let content = base64Parts.join('')
      const rem = content.length % 4
      if (rem) content += '='.repeat(4 - rem)

      // Emit to app
      const result = rogue.emitToApp('open_document', {
        title         : filename,
        fileExtension : extension,
        content,
        url           : getProductLink() || window.location.toString(),
      })

      // Cleanup
      base64Parts.length = 0
      remainderBytes = []
      content = ''
      await new Promise(resolve => setTimeout(resolve, 0))

      return result
    } catch (error) {
      rogue.log.error('Error processing model for AR export:', error)
      throw error
    }
  } : undefined

  if (iosAR) {
    rogue.log.info('Loading in iOS')

    try {
      const productLink = getProductLink() || window.location.toString()
      rogue.track.track({ type: 'AR', step: 'iOS Viewer', arAction: 'launch', link: productLink, sid, sku: productSku.value })

      sid = sid || (Math.random() + 1).toString(36).substring(10)

      rogue.log.crumb('AR', 'Launching iOS ar viewer')
      rogue.log.info('Loading XR for builder')

      const shareUrl = `${productLink}${productLink.indexOf('?') === -1 ? '?' : '&'}sid=${sid}`
      
      if (!activeEngine.engine) {
        await activeEngine.init()
      }
      if (!cachedEngine && rackbuilderLoading.value) {
        await rack.initialRenderComplete
      }
      if (!activeEngine.builders[0]) {
        throw new Error('No rackbuilder found in engine')
      }

      const xrModule = await import(/* webpackChunkName: "xr" */'@rogue/rackbuilder/xr') as any

      rogue.log.info('Preparing to export')

      const exportOptions: any = {
        shareUrl,
        dataCallback,
        useDirectDownload,
      }

      await xrModule.exportUSDZFile(activeEngine.builders[0], exportOptions)

      loading.value = false
    } catch (error) {
      rogue.log.error('iOS AR export failed', error)
      loading.value = false
      // Fallback to QR code on iOS export failure
      const qrGenerated = await generateQRCode()
      if (qrGenerated) {
        uiStore.showOverlay('ar-qr', rogue)
      }
    }
  } else if (hasXR || (isAndroidMobile && (navigator as any).xr)) {
    rogue.log.info(`Attempting WebXR - hasXR: ${hasXR}, isAndroidMobile: ${isAndroidMobile}`)
    try {
      const xrModule = await import(/* webpackChunkName: "xr" */'@rogue/rackbuilder/xr') as any
      rogue.track.track({ type: 'AR', step: 'WebXR', arAction: 'launch', sid, sku: productSku.value })
      rogue.log.crumb('AR', 'Launching webxr viewer')
      if (xr.value) {
        rogue.log.info('Disposing existing XR module')
        xr.value?.dispose()
      }

      await activeEngine.init()

      xr.value = new xrModule.RogueXRModule(activeEngine.engine!, { overlayEl: overlayTutorial.value })
      xr.value?.events.on('xrToggled', (val: boolean) => {
        nextTick(() => {
          loading.value = val
          rogue.track.track({ type: 'AR', step: 'WebXR', arAction: val ? 'launch' : 'close', link: getProductLink() || '', sid, sku: productSku.value })
          if (!val) {
            showArOverlay.value = false
          }
        })
      })

      nextTick(() => showArOverlay.value = true)

      await xr.value?.toggleXR(true)
    } catch (error) {
      rogue.log.error('WebXR initialization failed', error)
      loading.value = false
      const qrGenerated = await generateQRCode()
      if (qrGenerated) {
        uiStore.showOverlay('ar-qr', rogue)
      }
    }
  } else if (isAndroidApp) {
    rogue.log.info('Loading in Android')

    try {
      const productLink = getProductLink() || window.location.toString()
      rogue.track.track({ type: 'AR', step: 'Android Viewer', arAction: 'launch', link: productLink, sid })

      sid = sid || (Math.random() + 1).toString(36).substring(10)

      rogue.log.crumb('AR', 'Launching Android ar viewer')
      rogue.log.info('Loading XR for builder')

      const shareUrl = `${productLink}${productLink.indexOf('?') === -1 ? '?' : '&'}sid=${sid}`
      
      if (!activeEngine.engine) {
        await activeEngine.init()
      }
      if (!cachedEngine && rackbuilderLoading.value) {
        await rack.initialRenderComplete
      }
      if (!activeEngine.builders[0]) {
        throw new Error('No rackbuilder found in engine')
      }

      const xrModule = await import(/* webpackChunkName: "xr" */'@rogue/rackbuilder/xr') as any

      rogue.log.info('Preparing to export')

      const exportOptions: any = {
        shareUrl,
        dataCallback,
      }

      await xrModule.exportGLBFile(activeEngine.builders[0], exportOptions)

      loading.value = false
    } catch (error) {
      rogue.log.error('Android AR export failed', error)
      loading.value = false
    }
  } else {
    const qrGenerated = await generateQRCode()

    await new Promise(resolve => setTimeout(resolve, 250))

    if (qrGenerated) {
      uiStore.showOverlay('ar-qr', rogue)
    }

    loading.value = false
  }
}

onBeforeUnmount(() => {
  engine.dispose()
})

onMounted(() => {
  if (route.query.AR === '1') {
    loading.value = true
  }
})

</script>
<style lang="scss" scoped>
  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    display: none;
    pointer-events: none;

    &.visible {
      display: block;
    }
  }
  .btn-content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-weight: $weight-bold;
  }

  .link {
    font-size: $size-xxs;
    color: $color-gray-x-dark;
    margin-bottom: $space-xxs;
  }

  .ar-icon {
    width: 24px;
    height: 24px;
    margin-right: $space-xxs;
  }

  .qr-img {
    width: 100%;
    max-width: 100%;
  }

  .ar-modal {
    z-index: 3;
  }

  .ar-modal h1 {
    margin-bottom: $space-s;
  }

  .ar-modal-content {
    padding: $space-s;
    max-height: 80vh;
    overflow-y: auto;
  }

  .threed-viewer {
    visibility: hidden;
    height: 1px;
    overflow: hidden;

    .threejs {
      width: 1px;
      height: 1px;
      visibility: hidden;
    }

    .loading {
      width: 100%;
      height: 100%;
      display: flex;
      pointer-events: none;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 0;
      left: 0;
    }

    &.showing {
      visibility: visible;
      position: fixed;
      height: auto;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 9999999;
      background-color: white;

      .threejs {
        width: 100%;
        height: 100%;
        visibility: visible;
      }
    }
  }

  .modal-close-button {
    position: absolute;
    top: $space-s;
    right: $space-s;
    cursor: pointer;
  }

  .buttons {
    display: flex;

    .three-d {
      margin-left: $space-s;
    }
  }

  @include mobile(){
    .view-in-ar {
      margin-top:  $space-s
    }
  }
</style>

<style lang="scss" scoped target="app">
.platform-ios, .platform-android {
  .buttons {
    .three-d {
      margin-bottom: $app-gap-default;
    }
  }
}
</style>

